// ============================================================================
//  Facilities.js — Admin Panel · Facility Onboarding.
//  Register the external facilities we do claims for. Each facility carries
//  a name, logo, NHIS facility level (1–10), registration date, contact info,
//  and — once assigned — a live count of the services / investigations /
//  procedures / medicines that level entitles them to.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import notify from './notify';
import Modal from './Modal';
import {
  adminClientFacilities, adminClientFacilityById, adminClientFacilityEntitle,
  adminClientFacilityCccVerify,
  nhisFacilityLevels, assetUrl,
} from '../BaseURL';
import { IconSearch, IconClose, IconHR } from './icons';
import { AdminHeader, AdminCard, AdminEmpty, AdminToolbar } from './AdminSection';
import './Admin.css';
import './Facilities.css';

export default function Facilities() {
  const [rows, setRows]     = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]     = useState('');
  const [tier, setTier]       = useState('all');
  const [editing, setEditing] = useState(null); // null | 'new' | facility row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, l] = await Promise.all([
        axios.get(adminClientFacilities),
        axios.get(nhisFacilityLevels),
      ]);
      setRows(f.data.facilities || []);
      setLevels(l.data.levels || []);
    } catch (err) {
      notify.error('Failed to load', err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (tier !== 'all' && r.level_tier !== tier) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        r.name?.toLowerCase().includes(q) ||
        r.short_code?.toLowerCase().includes(q) ||
        r.location?.toLowerCase().includes(q) ||
        r.nhis_provider_id?.toLowerCase().includes(q) ||
        r.level_name?.toLowerCase().includes(q)
      );
    });
  }, [rows, query, tier]);

  const removeFacility = async (f) => {
    const ok = await notify.confirm(
      'Remove facility?',
      `${f.name} will be removed from the onboarding list. Claims history is unaffected.`,
      { confirmText: 'Remove', cancelText: 'Cancel' }
    );
    if (!ok) return;
    try {
      await axios.delete(adminClientFacilityById(f.id));
      setRows((list) => list.filter((x) => x.id !== f.id));
      notify.toast.success('Facility removed');
    } catch (err) {
      notify.error('Delete failed', err?.response?.data?.error || err.message);
    }
  };

  const onSaved = (saved, isNew) => {
    setRows((list) => isNew
      ? [saved, ...list]
      : list.map((x) => (x.id === saved.id ? saved : x))
    );
    setEditing(null);
  };

  return (
    <div className="ap">
      <AdminHeader
        title="Facility Onboarding"
        description="Register the facilities we do claims for. Assigning a level automatically unlocks what services / investigations / medicines they can bill."
        count={rows.length}
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <IconHR size={16} /> <span>Onboard facility</span>
          </button>
        }
      />

      <AdminToolbar>
        <div className="ap-search">
          <IconSearch size={18} />
          <input
            type="text"
            placeholder="Search name, short code, location or NHIS provider ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="ap-icon-btn" onClick={() => setQuery('')} aria-label="Clear">
              <IconClose size={12} />
            </button>
          )}
        </div>
        {['all', 'tertiary', 'secondary', 'primary', 'other'].map((t) => (
          <button
            key={t}
            className={`ap-chip ${tier === t ? 'is-active' : ''}`}
            onClick={() => setTier(t)}
          >
            {t === 'all' ? 'All tiers' : t}
          </button>
        ))}
      </AdminToolbar>

      <AdminCard>
        {loading ? (
          <AdminEmpty title="Loading facilities…" body="One moment." icon={<IconHR size={24} />} />
        ) : filtered.length === 0 ? (
          <AdminEmpty
            title={rows.length === 0 ? 'No facilities onboarded yet' : 'No matches'}
            body={rows.length === 0
              ? 'Click Onboard facility to add your first client facility.'
              : 'Try clearing the search or the tier filter.'}
            icon={<IconHR size={24} />}
          />
        ) : (
          <div className="fac-grid">
            {filtered.map((f) => (
              <FacilityCard
                key={f.id}
                facility={f}
                onEdit={() => setEditing(f)}
                onDelete={() => removeFacility(f)}
              />
            ))}
          </div>
        )}
      </AdminCard>

      {editing && (
        <FacilityFormModal
          facility={editing === 'new' ? null : editing}
          levels={levels}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────
function FacilityCard({ facility, onEdit, onDelete }) {
  const logo = assetUrl(facility.logo_path);
  const initials = (facility.name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const registered = facility.registration_date
    ? new Date(facility.registration_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';
  return (
    <div className={`fac-card ${facility.is_active ? '' : 'is-inactive'}`}>
      <div className="fac-card-head">
        <div className="fac-card-logo">
          {logo
            ? <img src={logo} alt="" />
            : <span>{initials}</span>}
        </div>
        <div className="fac-card-copy">
          <div className="fac-card-name" title={facility.name}>{facility.name}</div>
          <div className="fac-card-sub">
            {facility.short_code && <span className="fac-code">{facility.short_code}</span>}
            {facility.location && <span> · {facility.location}</span>}
          </div>
        </div>
      </div>

      <div className="fac-card-level">
        {facility.level_code ? (
          <>
            <span className={`fac-tier fac-tier-${facility.level_tier || 'other'}`}>
              Level {facility.level_code}
            </span>
            <span className="fac-level-name" title={facility.level_name}>{facility.level_name}</span>
          </>
        ) : (
          <span className="fac-tier fac-tier-none">No level set</span>
        )}
      </div>

      <div className="fac-card-meta">
        <span>Registered {registered}</span>
        {!facility.is_active && <span className="fac-tag">Disabled</span>}
      </div>

      <div className="fac-card-actions">
        <button className="btn btn-ghost btn-xs" onClick={onEdit}>Manage</button>
        <button className="btn btn-ghost btn-xs" onClick={onDelete} style={{ color: 'var(--danger)' }}>
          Remove
        </button>
      </div>
    </div>
  );
}

// ─── Create / edit modal ────────────────────────────────────────────────────
function FacilityFormModal({ facility, levels, onClose, onSaved }) {
  const isNew = !facility;
  const fileInput = useRef(null);
  const [saving, setSaving] = useState(false);
  const [entitle, setEntitle] = useState(null);

  const [form, setForm] = useState(() => ({
    name:             facility?.name || '',
    shortCode:        facility?.short_code || '',
    facilityLevelId:  facility?.facility_level_id || '',
    registrationDate: facility?.registration_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    location:         facility?.location || '',
    address:          facility?.address || '',
    phone:            facility?.phone || '',
    email:            facility?.email || '',
    website:          facility?.website || '',
    nhisProviderId:   facility?.nhis_provider_id || '',
    contactPerson:    facility?.contact_person || '',
    contactPhone:     facility?.contact_phone || '',
    contactEmail:     facility?.contact_email || '',
    notes:            facility?.notes || '',
    nhiaApiBaseUrl:   facility?.nhia_api_base_url   || 'https://elig.nhia.gov.gh:5000',
    nhiaApiEndpoint:  facility?.nhia_api_endpoint   || '/api/hmis/genCCC',
    nhiaApiKey:       facility?.nhia_api_key        || '',
    nhiaApiSecret:    '',
    isActive:         facility ? !!facility.is_active : true,
  }));
  const [showSecret, setShowSecret] = useState(false);
  const [verifying, setVerifying]   = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(facility ? assetUrl(facility.logo_path) : null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load entitlements when the assigned level changes (or on open, for existing).
  useEffect(() => {
    if (!facility || !form.facilityLevelId) { setEntitle(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(adminClientFacilityEntitle(facility.id));
        if (!cancelled) setEntitle(data);
      } catch { if (!cancelled) setEntitle(null); }
    })();
    return () => { cancelled = true; };
  }, [facility, form.facilityLevelId]);

  const onLogoPick = (file) => {
    if (!file) { setLogoFile(null); setLogoPreview(null); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const phoneValid = !form.phone || isValidPhoneNumber(form.phone);
  const contactPhoneValid = !form.contactPhone || isValidPhoneNumber(form.contactPhone);

  const canSubmit = form.name.trim().length >= 2 && phoneValid && contactPhoneValid;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') fd.append(k, v);
      });
      if (logoFile) fd.append('logo', logoFile);

      const url = isNew ? adminClientFacilities : adminClientFacilityById(facility.id);
      const { data } = await axios({
        method: isNew ? 'post' : 'put',
        url,
        data: fd,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      notify.toast.success(isNew ? 'Facility onboarded' : 'Facility updated');
      onSaved?.(data.facility, isNew);
    } catch (err) {
      notify.error(isNew ? 'Onboard failed' : 'Update failed', err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isNew ? 'Onboard a facility' : `Manage ${facility.name}`}
      subtitle={isNew
        ? 'Fill in what you know now — you can always come back and add more.'
        : 'Update the record and see what its level entitles it to.'}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!canSubmit || saving}>
            {saving ? 'Saving…' : isNew ? 'Onboard' : 'Save changes'}
          </button>
        </>
      }
    >
      {/* ── Logo picker ──────────────────────────────────────── */}
      <div className="fac-logo-row">
        <div className="fac-logo-preview" onClick={() => fileInput.current?.click()}>
          {logoPreview
            ? <img src={logoPreview} alt="" />
            : <div className="fac-logo-placeholder">Logo</div>}
        </div>
        <div className="fac-logo-copy">
          <div className="fac-logo-title">Facility logo</div>
          <div className="fac-logo-sub">PNG, JPG, WEBP or SVG · up to 5&nbsp;MB</div>
          <div className="fac-logo-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileInput.current?.click()}>
              {logoPreview ? 'Change logo' : 'Upload logo'}
            </button>
            {logoPreview && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onLogoPick(null)}>
                Remove
              </button>
            )}
          </div>
          <input ref={fileInput} type="file" accept="image/*" hidden
            onChange={(e) => onLogoPick(e.target.files?.[0])} />
        </div>
      </div>

      {/* ── Core details ─────────────────────────────────────── */}
      <div className="field">
        <label>Facility name</label>
        <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)}
          placeholder="e.g. St. Michael Medical Centre" autoFocus />
      </div>

      <div className="field-row">
        <div className="field">
          <label>NHIS facility level</label>
          <select value={form.facilityLevelId} onChange={(e) => set('facilityLevelId', e.target.value)}>
            <option value="">— pick a level —</option>
            {levels.map((l) => (
              <option key={l.id} value={l.id}>Level {l.code} · {l.name}</option>
            ))}
          </select>
          <span className="field-hint">Drives what services, investigations and medicines the facility can bill.</span>
        </div>
        <div className="field">
          <label>Registration date</label>
          <input className="input" type="date" value={form.registrationDate}
            onChange={(e) => set('registrationDate', e.target.value)} />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label>Short code (optional)</label>
          <input className="input" value={form.shortCode} onChange={(e) => set('shortCode', e.target.value)}
            placeholder="e.g. SMMC" />
        </div>
        <div className="field">
          <label>NHIS provider ID (optional)</label>
          <input className="input" value={form.nhisProviderId} onChange={(e) => set('nhisProviderId', e.target.value)}
            placeholder="e.g. 12345" />
        </div>
      </div>

      {/* ── Location + contact ───────────────────────────────── */}
      <div className="field-row">
        <div className="field">
          <label>City / Town</label>
          <input className="input" value={form.location} onChange={(e) => set('location', e.target.value)}
            placeholder="e.g. Accra" />
        </div>
        <div className="field">
          <label>Phone</label>
          <PhoneInput
            international
            defaultCountry="GH"
            countryCallingCodeEditable={false}
            placeholder="Enter phone number"
            value={form.phone}
            onChange={(v) => set('phone', v || '')}
            className={`phone-input ${form.phone && !phoneValid ? 'is-error' : ''}`}
          />
        </div>
      </div>

      <div className="field">
        <label>Address (optional)</label>
        <textarea className="input" rows={2} value={form.address}
          onChange={(e) => set('address', e.target.value)}
          style={{ resize: 'vertical', minHeight: 60, fontFamily: 'inherit' }} />
      </div>

      <div className="field-row">
        <div className="field">
          <label>Email (optional)</label>
          <input className="input" type="email" value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="info@facility.com" />
        </div>
        <div className="field">
          <label>Website (optional)</label>
          <input className="input" value={form.website} onChange={(e) => set('website', e.target.value)}
            placeholder="https://…" />
        </div>
      </div>

      {/* ── Primary contact person ───────────────────────────── */}
      <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--text-muted)',
                    margin: '18px 0 8px' }}>
        Primary contact
      </div>
      <div className="field-row">
        <div className="field">
          <label>Contact person</label>
          <input className="input" value={form.contactPerson}
            onChange={(e) => set('contactPerson', e.target.value)}
            placeholder="Full name" />
        </div>
        <div className="field">
          <label>Contact phone</label>
          <PhoneInput
            international
            defaultCountry="GH"
            countryCallingCodeEditable={false}
            placeholder="Contact phone"
            value={form.contactPhone}
            onChange={(v) => set('contactPhone', v || '')}
            className={`phone-input ${form.contactPhone && !contactPhoneValid ? 'is-error' : ''}`}
          />
        </div>
      </div>
      <div className="field">
        <label>Contact email</label>
        <input className="input" type="email" value={form.contactEmail}
          onChange={(e) => set('contactEmail', e.target.value)}
          placeholder="contact@facility.com" />
      </div>

      <div className="field">
        <label>Internal notes (optional)</label>
        <textarea className="input" rows={3} value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Anything worth remembering — special billing arrangements, etc."
          style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }} />
      </div>

      {/* ── NHIA CCC API credentials ──────────────────────────── */}
      <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--text-muted)',
                    margin: '18px 0 4px' }}>
        NHIA CCC API credentials
      </div>
      <p style={{ margin: '0 0 10px', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
        Used to verify NHIS/Ghana Card holders and generate a Claim Coverage Confirmation
        (CCC) for this facility. Registered with the NHIA per site.
      </p>
      <div className="field-row">
        <div className="field">
          <label>Base URL</label>
          <input className="input" value={form.nhiaApiBaseUrl}
            onChange={(e) => set('nhiaApiBaseUrl', e.target.value)}
            placeholder="https://elig.nhia.gov.gh:5000" />
        </div>
        <div className="field">
          <label>Endpoint</label>
          <input className="input" value={form.nhiaApiEndpoint}
            onChange={(e) => set('nhiaApiEndpoint', e.target.value)}
            placeholder="/api/hmis/genCCC" />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>API key (x-nhia-apikey)</label>
          <input className="input" value={form.nhiaApiKey}
            onChange={(e) => set('nhiaApiKey', e.target.value)}
            placeholder="e.g. HPAPI10801" />
        </div>
        <div className="field">
          <label>API secret (x-nhia-apisecret)</label>
          <div className="input-with-toggle">
            <input
              className="input"
              type={showSecret ? 'text' : 'password'}
              value={form.nhiaApiSecret}
              onChange={(e) => set('nhiaApiSecret', e.target.value)}
              placeholder={facility?.nhia_api_secret_hint
                ? `Leave blank to keep · ${facility.nhia_api_secret_hint}`
                : 'Paste the secret'}
              autoComplete="new-password"
            />
            <button type="button" className="toggle" onClick={() => setShowSecret((s) => !s)}>
              {showSecret ? 'Hide' : 'Show'}
            </button>
          </div>
          {facility?.nhia_api_secret_set && (
            <span className="field-hint">A secret is already saved. Leave blank to keep it.</span>
          )}
        </div>
      </div>

      {!isNew && facility?.nhia_api_secret_set && (
        <div className="fac-ccc-verify">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={async () => {
              setVerifying(true);
              setVerifyResult(null);
              try {
                const { data } = await axios.post(adminClientFacilityCccVerify(facility.id), {
                  cardType: 'NHISCARD', cardNo: '0000000000',
                });
                setVerifyResult(data);
                if (data.verified) notify.toast.success('NHIA API reachable');
                else notify.toast.warning('NHIA API responded ' + data.status);
              } catch (err) {
                setVerifyResult({ verified: false, status: err?.response?.status, body: err?.response?.data });
                notify.error('Verification failed', err?.response?.data?.error || err.message);
              } finally {
                setVerifying(false);
              }
            }}
            disabled={verifying}
          >
            {verifying ? 'Testing…' : 'Test CCC endpoint'}
          </button>
          {verifyResult && (
            <div className={`fac-ccc-result ${verifyResult.verified ? 'is-ok' : 'is-bad'}`}>
              <strong>{verifyResult.verified ? 'Reachable' : 'Failed'}</strong>
              {' · '}HTTP {verifyResult.status || '—'}
              {typeof verifyResult.elapsedMs === 'number' && <> · {verifyResult.elapsedMs} ms</>}
            </div>
          )}
        </div>
      )}

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
        <span style={{ fontSize: '0.9rem' }}>Active — receive claims for this facility</span>
      </label>

      {/* ── Entitlement summary (existing facilities only) ───── */}
      {!isNew && entitle?.counts && (
        <div className="fac-entitle">
          <div className="fac-entitle-head">
            <div>
              <div className="fac-entitle-eyebrow">Entitlements at</div>
              <div className="fac-entitle-title">
                Level {entitle.level?.code} · {entitle.level?.name}
              </div>
            </div>
          </div>
          <div className="fac-entitle-grid">
            <EntitleTile label="Main OPD services"     value={entitle.counts.opd_services}     accent="#22C55E" />
            <EntitleTile label="Services (surg./med.)" value={entitle.counts.services}         accent="#3B82F6" />
            <EntitleTile label="Investigations"        value={entitle.counts.investigations}   accent="#F59E0B" />
            <EntitleTile label="Procedures"            value={entitle.counts.procedures}       accent="#EC4899" />
            <EntitleTile label="Diagnoses covered"     value={entitle.counts.diagnoses}        accent="#6366F1" />
            <EntitleTile label="Medicines (explicit)"  value={entitle.counts.medicines_explicit} accent="#A855F7" />
          </div>
          <p className="fac-entitle-note">
            Entitlements are looked up from the master catalogue live — they update automatically
            whenever a G-DRG, diagnosis, or medicine gets a new level assigned.
          </p>
        </div>
      )}
    </Modal>
  );
}

function EntitleTile({ label, value, accent }) {
  return (
    <div className="fac-entitle-tile" style={{ '--acc': accent }}>
      <div className="fac-entitle-value">{Number(value || 0).toLocaleString()}</div>
      <div className="fac-entitle-label">{label}</div>
    </div>
  );
}
